import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Modal, Row, Col, DatePicker, Select, Form, Input, InputNumber } from 'antd';
import { Info, X, Plus, Pencil } from 'lucide-react';
import { Button as CrceButton, Button } from '@crc-evans/ui-components';
import get from 'lodash/get';
import moment from 'moment';
import { getPopupContainerBody } from './ShippingManagement/shipmentFieldOptions';
import AddPQTReportModal from './AddPQTReportModal';
import {
  fetchPqtElements,
  fetchProjectPqtReport,
  updateProjectPqtReport,
  createProjectPqtReport,
  createPqtElement,
} from '../redux/projectPqtReports/actions';
import './DSR/DownTimeModal.css';
import '../pages/dashboard/DashboardPage.css';
import { NDT_PIPELINE_TYPE_OPTIONS } from '../constants/ndtPipelineTypeOptions';
import { ndtTypeFormInitial, normalizeNdtTypeForApi } from '../utils/sowNdtStation';
import {
  getProjectDisplayFields,
  getSiteLocationLabel,
  mergeProjectConfigExtras,
  parseProjectConfig,
} from '../utils/projectConfig';
import ModernMultiCheckboxSelect from './common/ModernMultiCheckboxSelect';
import { fetchProject as fetchProjects } from '../redux/actions/projectAction';

const { Option } = Select;

const DATE_DROPDOWN = 'modern-dropdown-popup mc-dpr-datepicker-dropdown';
const SELECT_DROPDOWN = 'modern-dropdown-popup mc-dpr-modal-select-dd';
const PROJECT_DATE_FORMATS = ['MM-DD-YYYY', 'DD-MM-YYYY', 'YYYY-MM-DD', 'MM/DD/YYYY', 'DD/MM/YYYY'];

const parseProjectDate = (value) => {
  if (!value) return null;
  if (moment.isMoment(value)) return value.isValid() ? value : null;
  const strictParsed = moment(value, PROJECT_DATE_FORMATS, true);
  if (strictParsed.isValid()) return strictParsed;
  const fallback = moment(value);
  return fallback.isValid() ? fallback : null;
};

const getProjectDateValue = (project, type) => {
  if (!project) return null;
  const startCandidates = [project.startDate, project.start_date, project.projectStartDate];
  const endCandidates = [project.endDate, project.end_date, project.projectEndDate];
  const candidates = type === 'start' ? startCandidates : endCandidates;
  return candidates.find((item) => item != null && item !== '') || null;
};

const ProjectDetailsModal = (props) => {
  const {
    selectedProject,
    onUpdateProject,
    onReloadProjects,
    projectTeamMembersAccessList = [],
    userAccessList = [],
    pqtElements,
    pqtReport,
    isPqtReportLoading,
    isReportFetching,
    onFetchPqtElements,
    onFetchProjectPqtReport,
    onUpdateProjectPqtReport,
    onCreateProjectPqtReport,
    onCreatePqtElement,
    clientList,
  } = props;

  const isAdmin = userAccessList.includes('admin') || userAccessList.includes('super-admin');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isPqtReportModalVisible, setIsPqtReportModalVisible] = useState(false);
  const [startDateInput, setStartDateInput] = useState(
    parseProjectDate(getProjectDateValue(selectedProject, 'start'))
  );
  const [endDateInput, setEndDateInput] = useState(
    parseProjectDate(getProjectDateValue(selectedProject, 'end'))
  );
  const [dayShiftInputHour, setDayShiftInputHour] = useState(
    selectedProject?.config?.technicalLogDefaultShiftHours?.day || 0
  );
  const [nightShiftInputHour, setNightShiftInputHour] = useState(
    selectedProject?.config?.technicalLogDefaultShiftHours?.night || 0
  );
  const [ndtRejectMaxPercent, setNdtRejectMaxPercent] = useState(undefined);

  const [projectForm] = Form.useForm();
  const [editProjectType, setEditProjectType] = useState('');
  const [editNdtType, setEditNdtType] = useState(undefined);
  const [editFinalClient, setEditFinalClient] = useState('');
  const [editProjectSiteType, setEditProjectSiteType] = useState(undefined);

  const siteLocationLabel = getSiteLocationLabel(editProjectSiteType);

  const projectId = selectedProject?.id;

  useEffect(() => {
    if (!isModalVisible || !selectedProject?.id) return;
    const start = parseProjectDate(getProjectDateValue(selectedProject, 'start'));
    const end = parseProjectDate(getProjectDateValue(selectedProject, 'end'));
    const day = selectedProject?.config?.technicalLogDefaultShiftHours?.day ?? 0;
    const night = selectedProject?.config?.technicalLogDefaultShiftHours?.night ?? 0;
    const display = getProjectDisplayFields(selectedProject);
    setStartDateInput(start);
    setEndDateInput(end);
    setDayShiftInputHour(day);
    setNightShiftInputHour(night);
    const cfg = parseProjectConfig(selectedProject.config);
    const pt = selectedProject?.projectType || '';
    const showNdtReject = pt === 'Welding' || pt === 'Both';
    setEditProjectType(pt);
    setEditNdtType(ndtTypeFormInitial(selectedProject));
    setEditFinalClient(selectedProject?.finalClient || '');
    setEditProjectSiteType(display.projectSiteType || undefined);
    projectForm.setFieldsValue({
      clientName: selectedProject.clientName,
      manager: selectedProject.manager,
      location: display.country,
      subcontractor: display.subcontractor,
      projectSiteType: display.projectSiteType || undefined,
      siteLocationDetail: display.siteLocationDetail,
      crceRepresentative: display.crceRepresentative,
      startDate: start,
      endDate: end,
      projectStatus: selectedProject.projectStatus || 1,
      completedWelds: selectedProject.completedWelds != null ? String(selectedProject.completedWelds) : '-',
      technicianLog: `${day} Hour / ${night} Hour`,
      projectType: pt || undefined,
      ndtType: ndtTypeFormInitial(selectedProject),
      ndtRejectMaxPercent:
        showNdtReject && cfg.ndtRejectMaxPercent != null && cfg.ndtRejectMaxPercent !== ''
          ? Number(cfg.ndtRejectMaxPercent)
          : undefined,
      finalClient: selectedProject?.finalClient || '',
    });
  }, [isModalVisible, selectedProject, projectForm]);

  useEffect(() => {
    if (!isModalVisible || !projectId) return;
    onFetchPqtElements();
    onFetchProjectPqtReport(projectId);
  }, [isModalVisible, projectId, onFetchPqtElements, onFetchProjectPqtReport]);

  const isButtonDisabled = () => {
    if (userAccessList.includes('admin') || userAccessList.includes('super-admin')) return false;
    if (projectTeamMembersAccessList[0]?.isGuestUser) return true;
    if (
      projectTeamMembersAccessList[0]?.isNormalUser &&
      !projectTeamMembersAccessList[0]?.tabPermissions?.Details?.write
    ) {
      return true;
    }
    return false;
  };

  const isWriteAccess = isButtonDisabled();

  const handleSavePqtReport = async (payload) => {
    const id = get(selectedProject, 'id', undefined);
    if (payload.reportId) {
      await onUpdateProjectPqtReport({
        reportId: payload.reportId,
        name: payload.name,
        description: payload.description,
        elements: payload.elements,
      });
    } else {
      await onCreateProjectPqtReport({
        projectId: id,
        name: payload.name,
        description: payload.description,
        elements: payload.elements,
      });
    }
    setTimeout(() => {
      if (id) onFetchProjectPqtReport(id);
      setIsPqtReportModalVisible(false);
    }, 1500);
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditProjectType('');
    setEditNdtType(undefined);
    setEditFinalClient('');
    setEditProjectSiteType(undefined);
  };

  const handleSave = async () => {
    try {
      const values = await projectForm.validateFields();
      const effectiveStartDate = startDateInput || values.startDate;
      const effectiveEndDate = endDateInput || values.endDate;
      if (projectId) {
        const prevConfig = parseProjectConfig(selectedProject?.config);
        const projectTypeVal = values.projectType ?? editProjectType ?? '';
        const weldingOrBoth =
          projectTypeVal === 'Welding' || projectTypeVal === 'Both';
        const ndtRejectRaw = values.ndtRejectMaxPercent;
        const ndtRejectNum =
          ndtRejectRaw !== undefined && ndtRejectRaw !== null && ndtRejectRaw !== ''
            ? Number(ndtRejectRaw)
            : undefined;

        const nextConfig = mergeProjectConfigExtras(prevConfig, {
          subcontractor: values.subcontractor,
          projectSiteType: values.projectSiteType ?? editProjectSiteType,
          siteLocationDetail: values.siteLocationDetail,
          crceRepresentative: values.crceRepresentative,
        });
        nextConfig.technicalLogDefaultShiftHours = {
          day: dayShiftInputHour,
          night: nightShiftInputHour,
        };
        if (
          weldingOrBoth &&
          ndtRejectNum !== undefined &&
          !Number.isNaN(ndtRejectNum)
        ) {
          nextConfig.ndtRejectMaxPercent = ndtRejectNum;
        } else {
          delete nextConfig.ndtRejectMaxPercent;
        }

        const updateData = {
          startDate: moment(effectiveStartDate).format('MM-DD-YYYY'),
          endDate: moment(effectiveEndDate).format('MM-DD-YYYY'),
          projectStatus: values.projectStatus,
          projectType: projectTypeVal,
          finalClient: values.finalClient ?? editFinalClient ?? '',
          location: values.location,
          contractor: values.clientName ?? selectedProject?.clientName,
          ndtType:
            normalizeNdtTypeForApi(values.ndtType) ??
            normalizeNdtTypeForApi(editNdtType),
          config: nextConfig,
        };
        await onUpdateProject({ data: updateData, id: projectId });
      }
      // Ensure any project list views reflect the update immediately.
      if (typeof onReloadProjects === 'function') {
        onReloadProjects();
      }
      setIsModalVisible(false);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  return (
    <>
      <CrceButton
        label="Project Details"
        variant="primary"
        size="medium"
        icon={Info}
        onClick={showModal}
      />

      <Modal
        width="min(92vw, 960px)"
        className="modern-create-project-modal modern-dsr-dt-add-modal modern-project-details-modal"
        wrapClassName="modern-dsr-dt-add-modal__wrap"
        title={(
          <h3 className="modern-dsr-dt-add-modal__h-title">
            {selectedProject?.projectName
              ? `Project: ${selectedProject.projectName}`
              : 'Project details'}
          </h3>
        )}
        closeIcon={(
          <span className="modern-dsr-dt-add-modal__close-icon" aria-hidden>
            <X size={20} strokeWidth={2} />
          </span>
        )}
        centered
        visible={isModalVisible}
        maskStyle={{ background: 'rgba(0, 0, 0, 0.5)' }}
        onCancel={handleCancel}
        bodyStyle={{ maxHeight: 'min(85vh, 900px)', overflowY: 'auto' }}
        footer={(
          <div
            className="modern-dsr-dt-add-modal__foot"
            role="group"
            aria-label="Project details actions"
          >
            <CrceButton
              label="Cancel"
              variant="tertiary"
              size="medium"
              onClick={handleCancel}
            />
            <CrceButton
              label="Save"
              variant="primary"
              size="medium"
              onClick={handleSave}
            />
          </div>
        )}
      >
        <div className="modern-dsr-tech-log-section modern-dsr-dt-add-modal__form-scope">
          <Form
            form={projectForm}
            className="modern-dsr-dt-add-modal__form modern-project-details-modal__form"
            name="project-details"
            layout="vertical"
            autoComplete="off"
          >
            <Row gutter={[18, 4]} className="modern-project-details-modal__grid">
              <Col xs={24} sm={12} md={12} lg={8}>
                <Form.Item
                  label="Client / Contractor"
                  name="clientName"
                  initialValue={selectedProject?.clientName}
                >
                  <Input
                    placeholder="Client / Contractor"
                    size="large"
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={8}>
                <Form.Item label="Company" name="finalClient">
                  <Select
                    className="client-select"
                    placeholder="Select Company"
                    size="large"
                    dropdownClassName={SELECT_DROPDOWN}
                    getPopupContainer={getPopupContainerBody}
                    showSearch
                    allowClear
                    optionFilterProp="children"
                    value={editFinalClient || undefined}
                    onChange={(v) => setEditFinalClient(v)}
                    filterOption={(input, option) =>
                      String(option?.children ?? '')
                        .toLowerCase()
                        .includes(String(input).toLowerCase())
                    }
                  >
                    {(clientList || []).map((item, i) => (
                      <Option key={i} value={item && item.name}>
                        {item && item.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={8}>
                <Form.Item label="Sub-Contractor" name="subcontractor">
                  <Input placeholder="Sub-Contractor" size="large" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={8}>
                <Form.Item label="Offshore / Onshore" name="projectSiteType" required>
                  <Select
                    className="client-select"
                    placeholder="Select Offshore or Onshore"
                    size="large"
                    dropdownClassName={SELECT_DROPDOWN}
                    getPopupContainer={getPopupContainerBody}
                    value={editProjectSiteType}
                    onChange={(v) => setEditProjectSiteType(v)}
                  >
                    <Option value="Offshore">Offshore</Option>
                    <Option value="Onshore">Onshore</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={8}>
                <Form.Item label={siteLocationLabel} name="siteLocationDetail" required>
                  <Input placeholder={siteLocationLabel} size="large" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={8}>
                <Form.Item label="Country" name="location" required>
                  <Input placeholder="Country" size="large" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={8}>
                <Form.Item
                  label="CRCE Manager"
                  name="manager"
                  initialValue={selectedProject?.manager}
                >
                  <Input
                    placeholder="CRCE Manager"
                    size="large"
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={8}>
                <Form.Item label="CRCE Representative" name="crceRepresentative" required>
                  <Input placeholder="On-board / on-site representative" size="large" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={8}>
                <Form.Item
                  className="date-picker"
                  label="Initial Start Date"
                  name="startDate"
                  initialValue={startDateInput}
                  required
                >
                  <DatePicker
                    value={startDateInput}
                    allowClear={false}
                    format="DD-MM-YYYY"
                    className="w-100"
                    style={{ width: '100%' }}
                    dropdownClassName={DATE_DROPDOWN}
                    getPopupContainer={getPopupContainerBody}
                    onChange={(date) => {
                      setStartDateInput(date);
                      projectForm.setFieldsValue({ startDate: date });
                    }}
                    disabled={!isAdmin}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={8}>
                <Form.Item
                  className="date-picker"
                  label="Initial End Date"
                  name="endDate"
                  initialValue={endDateInput}
                >
                  <DatePicker
                    value={endDateInput}
                    format="DD-MM-YYYY"
                    allowClear={false}
                    className="w-100"
                    style={{ width: '100%' }}
                    dropdownClassName={DATE_DROPDOWN}
                    getPopupContainer={getPopupContainerBody}
                    onChange={(date) => {
                      setEndDateInput(date);
                      projectForm.setFieldsValue({ endDate: date });
                    }}
                    disabledDate={(current) =>
                      startDateInput && current && current < startDateInput.clone().startOf('day')
                    }
                    disabled={!isAdmin}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={8}>
                <Form.Item
                  label="Project Status"
                  name="projectStatus"
                  initialValue={selectedProject?.projectStatus || 1}
                >
                  <Select
                    className="client-select"
                    disabled={!isAdmin}
                    size="large"
                    dropdownClassName={SELECT_DROPDOWN}
                    getPopupContainer={getPopupContainerBody}
                  >
                    <Option value={1}>Initiated</Option>
                    <Option value={2}>Active</Option>
                    <Option value={3}>On Hold</Option>
                    <Option value={4}>Completed</Option>
                    <Option value={5}>Facility</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={8}>
                <Form.Item
                  label="Completed Welds"
                  name="completedWelds"
                  initialValue="-"
                >
                  <Input
                    placeholder="Completed Welds"
                    size="large"
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={8}>
                <Form.Item
                  label="Technician Log Default Day/Night Shift"
                  name="technicianLog"
                  initialValue={`${dayShiftInputHour} Hour / ${nightShiftInputHour} Hour`}
                >
                  <Input
                    value={`${dayShiftInputHour} Hour / ${nightShiftInputHour} Hour`}
                    size="large"
                    disabled={!isAdmin}
                    onChange={(e) => {
                      if (!isAdmin) return;
                      const [day, night] = e.target.value.split(' / ').map((val) => parseInt(val, 10));
                      if (!Number.isNaN(day)) setDayShiftInputHour(day);
                      if (!Number.isNaN(night)) setNightShiftInputHour(night);
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={8}>
                <Form.Item label="Project Type" name="projectType" required>
                  <Select
                    className="client-select"
                    placeholder="Select project type"
                    size="large"
                    dropdownClassName={SELECT_DROPDOWN}
                    getPopupContainer={getPopupContainerBody}
                    value={editProjectType || undefined}
                    onChange={(v) => setEditProjectType(v)}
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      String(option?.children ?? '')
                        .toLowerCase()
                        .includes(String(input).toLowerCase())
                    }
                  >
                    <Option value="Welding">Welding</Option>
                    <Option value="Coating">Coating</Option>
                    <Option value="Both">Both</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={8}>
                <Form.Item label="NDT Type" name="ndtType">
                  <ModernMultiCheckboxSelect
                    value={editNdtType || undefined}
                    onChange={(v) => setEditNdtType(v)}
                    options={NDT_PIPELINE_TYPE_OPTIONS}
                    placeholder="Select NDT type(s)"
                  />
                </Form.Item>
              </Col>
              {(editProjectType === 'Welding' || editProjectType === 'Both') && (
                <Col xs={24} sm={12} md={12} lg={8}>
                  <Form.Item
                    label="Max NDT reject % (client limit)"
                    name="ndtRejectMaxPercent"
                    tooltip="NDT rejects must stay at or below this percentage per client specification."
                  >
                    <InputNumber
                      min={0}
                      max={100}
                      step={0.1}
                      controls={false}
                      placeholder="e.g. 2"
                      style={{ width: '100%' }}
                      size="large"
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>
          </Form>

          <div className="modern-project-details-pqt">
            <div className="modern-project-details-pqt__header">
              <div className="modern-project-details-pqt__titles">
                <h4 className="modern-project-details-pqt__title">PQT Planning</h4>
                <p className="modern-project-details-pqt__subtitle">
                  Manage Project Qualification Testing activities.
                </p>
              </div>
              {!pqtReport && (
                <Button
                  label="Add PQT Activities"
                  variant="primary"
                  size="small"
                  icon={Plus}
                  onClick={() => {
                    if (!isWriteAccess && selectedProject?.id) {
                      if (!pqtElements?.length) onFetchPqtElements();
                      onFetchProjectPqtReport(get(selectedProject, 'id'));
                      setIsPqtReportModalVisible(true);
                    }
                  }}
                  disabled={isWriteAccess}
                />
              )}
            </div>
            <div className="modern-project-details-pqt__box">
              {pqtReport ? (
                <div className="modern-project-details-pqt__row">
                  <div className="modern-project-details-pqt__report-text">
                    <div className="modern-project-details-pqt__name">{pqtReport.name}</div>
                    {pqtReport.description && (
                      <div className="modern-project-details-pqt__desc">{pqtReport.description}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="modern-project-details-pqt__edit"
                    onClick={() => !isWriteAccess && setIsPqtReportModalVisible(true)}
                    disabled={isWriteAccess}
                    title="Edit"
                    aria-label="Edit PQT"
                  >
                    <Pencil size={18} />
                  </button>
                </div>
              ) : (
                <p className="modern-project-details-pqt__empty">
                  No PQT planning. Click &quot;Add PQT Activities&quot; to create.
                </p>
              )}
            </div>
          </div>
        </div>
      </Modal>

      <AddPQTReportModal
        visible={isPqtReportModalVisible}
        onCancel={() => setIsPqtReportModalVisible(false)}
        onSave={handleSavePqtReport}
        pqtElements={pqtElements}
        existingReport={pqtReport}
        loading={isPqtReportLoading}
        isReportFetching={isReportFetching}
        isWriteAccess={isWriteAccess}
        onCreatePqtElement={onCreatePqtElement}
      />
    </>
  );
};

const mapStateToProps = ({ equip, projectPqtReportsReducer, clientRequest }) => ({
  pqtElements: get(projectPqtReportsReducer, 'pqtElements', []),
  pqtReport: get(projectPqtReportsReducer, 'report', null),
  isPqtReportLoading: get(projectPqtReportsReducer, 'isUpdateLoading', false),
  isReportFetching: get(projectPqtReportsReducer, 'isReportLoading', false),
  userAccessList: get(equip, 'userAccessList', []),
  clientList: get(clientRequest, 'clientData', []),
});

const mapDispatchToProps = {
  onFetchPqtElements: fetchPqtElements,
  onFetchProjectPqtReport: fetchProjectPqtReport,
  onUpdateProjectPqtReport: updateProjectPqtReport,
  onCreateProjectPqtReport: createProjectPqtReport,
  onCreatePqtElement: createPqtElement,
  onReloadProjects: fetchProjects,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProjectDetailsModal);
